/**
 * Runtime support detection for WebAuthn + PRF.
 *
 * Spec §3.7. Real PRF capability is only knowable from a completed ceremony
 * (via `clientExtensionResults.prf.enabled` or `prf.results.first`). This
 * pre-check returns the best signal we can get without spending a credential.
 */

/**
 * The key PRF occupies in a `PublicKeyCredential.getClientCapabilities()` map.
 *
 * Singular `extension:`, matching the rest of the family the map exposes
 * (`extension:appid`, `extension:credBlob`, `extension:hmacCreateSecret`, …).
 * There is no `extensions:` prefix in the map and there never was — a plural
 * spelling here is not a legacy form, it is a key no browser has ever
 * produced, and reading it makes the whole pre-check `undefined === false`,
 * i.e. permanently optimistic. That is exactly the bug #80 fixed.
 *
 * Exported so the pin in `tests-e2e/prf-capability-key.spec.ts` (in the wallet
 * repo -- not part of this package) can assert THIS value against a capability
 * map taken from a real browser, rather than against a literal a typo would
 * travel with.
 */
export const PRF_CAPABILITY_KEY = 'extension:prf';

export interface PrfSupportResult {
  webAuthn: boolean;
  /**
   * Whether a platform authenticator (Touch ID / Windows Hello) is enrolled.
   * INFORMATIONAL ONLY — it is NOT a precondition for using the wallet, which
   * also works with roaming security keys and phone passkeys (via hybrid).
   */
  platformAuthenticator: boolean;
  /**
   * Best-effort PRF signal. `true` unless we can prove otherwise: modern
   * browsers expose `PublicKeyCredential.getClientCapabilities()`, which gives
   * a definitive `extension:prf` answer. When that API is absent (older
   * browsers) we stay optimistic and let the ceremony be the final arbiter
   * (it throws `prf-unsupported` if the authenticator returns no PRF output).
   */
  prfPotentiallySupported: boolean;
}

export async function detectPrfSupport(): Promise<PrfSupportResult> {
  const PKC = (globalThis as any).PublicKeyCredential;
  const webAuthn = typeof PKC !== 'undefined';
  if (!webAuthn) {
    return { webAuthn: false, platformAuthenticator: false, prfPotentiallySupported: false };
  }

  let platformAuthenticator = false;
  try {
    platformAuthenticator =
      typeof PKC.isUserVerifyingPlatformAuthenticatorAvailable === 'function' &&
      (await PKC.isUserVerifyingPlatformAuthenticatorAvailable());
  } catch {
    platformAuthenticator = false;
  }

  // Definitive PRF capability when the browser supports getClientCapabilities
  // (Chrome 133+, Safari 18+). Only a hard `false` blocks; absence keeps us
  // optimistic. We deliberately do NOT require a platform authenticator here.
  let prfPotentiallySupported = true;
  try {
    if (typeof PKC.getClientCapabilities === 'function') {
      const caps = await PKC.getClientCapabilities();
      if (caps && caps[PRF_CAPABILITY_KEY] === false) {
        prfPotentiallySupported = false;
      }
    }
  } catch {
    // Keep optimistic; the ceremony will surface prf-unsupported if needed.
  }

  return { webAuthn, platformAuthenticator, prfPotentiallySupported };
}

/**
 * Inspect a `PublicKeyCredential.getClientExtensionResults()` payload to
 * determine whether PRF actually worked in the ceremony. Spec §3.7 calls this
 * "final answer; the pre-check above is best-effort only".
 */
export function isPrfSupportedResult(extensions: {
  prf?: { enabled?: boolean; results?: { first?: Uint8Array } };
}): boolean {
  const prf = extensions?.prf;
  if (!prf) return false;
  if (prf.enabled === true) return true;
  if (prf.results?.first instanceof Uint8Array) return true;
  return false;
}
